import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { loginRequest, registerRequest } from '../store/auth/actions';
import { RootState } from '../store/reducers';
import { Eye, EyeOff } from 'lucide-react';
import './Auth.css';

const Auth: React.FC = () => {
  const location = useLocation();
  const isRegister = location.pathname === '/inscription';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, isRegisterLoading, errors } = useSelector((state: RootState) => state.auth);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = { email, password };
    dispatch(loginRequest(payload, '/', navigate));
  };

  const handleRegister = (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPwd) {
      return;
    }
    const payload = { email, password, first_name: name, phone };
    dispatch(registerRequest(payload, 'register', navigate));
  };

  const handleSubmit = (e: React.FormEvent) => {
    if (isRegister) {
      handleRegister(e);
    } else {
      handleLogin(e);
    }
  };

  return (
    <div className="auth-page">
      {/* Left panel - branding */}
      <div className="auth-left-panel">
        <div className="auth-left-content">
          <div className="auth-logo">
            <div className="auth-logo-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10 9 9 9 8 9" />
              </svg>
            </div>
            <span className="auth-logo-text">DataGreffe</span>
          </div>
          <h2 className="auth-left-title">
            Votre plateforme d'annonces légales en Côte d'Ivoire
          </h2>
          <p className="auth-left-description">
            Accédez à l'ensemble des annonces légales publiées au Journal Officiel
            et dans Fraternité Matin. Recherchez, consultez et suivez les actes
            juridiques des entreprises ivoiriennes.
          </p>
          <div className="auth-left-features">
            <div className="auth-feature">
              <div className="auth-feature-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </div>
              <div>
                <strong>Recherche avancée</strong>
                <p>Trouvez rapidement les annonces par nom, RCCM ou mot-clé</p>
              </div>
            </div>
            <div className="auth-feature">
              <div className="auth-feature-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              </div>
              <div>
                <strong>Données fiables</strong>
                <p>Sources officielles vérifiées et mises à jour régulièrement</p>
              </div>
            </div>
            <div className="auth-feature">
              <div className="auth-feature-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                  <line x1="8" y1="21" x2="16" y2="21" />
                  <line x1="12" y1="17" x2="12" y2="21" />
                </svg>
              </div>
              <div>
                <strong>Accès en ligne</strong>
                <p>Consultez les annonces depuis n'importe quel appareil</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel - form */}
      <div className="auth-right-panel">
        <div className="auth-form-wrapper">
          <div className="auth-form-header">
            <h1 className="auth-form-title">
              {isRegister ? 'Créer un compte' : 'Se connecter'}
            </h1>
            <p className="auth-form-subtitle">
              {isRegister
                ? 'Remplissez les informations ci-dessous pour créer votre compte'
                : 'Entrez vos identifiants pour accéder à votre espace'}
            </p>
          </div>

          {errors && (
            <div className="auth-error-message">
              {typeof errors === 'string' ? errors : 'Une erreur est survenue'}
            </div>
          )}

          <form onSubmit={handleSubmit} className="auth-form">
            {isRegister && (
              <>
                <div className="form-group">
                  <label>Nom complet</label>
                  <input
                    type="text"
                    placeholder="Votre nom complet"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Téléphone</label>
                  <input
                    type="tel"
                    placeholder="+225 XX XX XX XX XX"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </div>
              </>
            )}

            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                placeholder="votre@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label>Mot de passe</label>
              <div className="input-password-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {isRegister && (
              <div className="form-group">
                <label>Confirmer le mot de passe</label>
                <div className="input-password-wrapper">
                  <input
                    type={showConfirmPwd ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={confirmPwd}
                    onChange={(e) => setConfirmPwd(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirmPwd(!showConfirmPwd)}
                  >
                    {showConfirmPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {password && confirmPwd && password !== confirmPwd && (
                  <span className="form-error-text">
                    Les mots de passe ne correspondent pas
                  </span>
                )}
              </div>
            )}

            {!isRegister && (
              <div className="form-forgot-link">
                <Link to="/mot-de-passe-oublie">Mot de passe oublié ?</Link>
              </div>
            )}

            <button
              type="submit"
              className="auth-submit-btn"
              disabled={isLoading || isRegisterLoading}
            >
              {(isLoading || isRegisterLoading)
                ? 'Chargement...'
                : isRegister
                  ? 'Créer mon compte'
                  : 'Se connecter'}
            </button>
          </form>

          <div className="auth-switch">
            {isRegister ? (
              <p>Vous avez déjà un compte ? <Link to="/connexion">Se connecter</Link></p>
            ) : (
              <p>Pas encore de compte ? <Link to="/inscription">Créer un compte</Link></p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
